/**
 * Forms API Service
 * Handles form submissions through Cloudflare Workers
 * 
 * Note: Forms should be submitted to Cloudflare Worker endpoints,
 * not directly to Bliss API (to keep API keys secure)
 */

import { getEnvConfig, isProduction } from '../utils/env';

export interface FormSubmissionResult {
  success: boolean;
  message?: string;
  error?: string;
}

function normalizeBaseUrl(value: string): string {
  return value.trim().replace(/\/+$/, '');
}

/**
 * Submit form data to Cloudflare Worker
 */
export async function submitForm(
  formType: 'volunteer' | 'parent-intake' | 'program-interest' | 'contact',
  formData: FormData | Record<string, unknown>
): Promise<FormSubmissionResult> {
  try {
    const env = getEnvConfig();
    const configuredProjectId = env.BLISS_PROJECT_ID?.trim();
    const configuredApiBase = env.BLISS_API_BASE_URL?.trim();

    if (formType === 'contact' && !Array.isArray(formData)) {
      const data = formData as Record<string, unknown>;
      const publicFormsUrl =
        isProduction() || !configuredProjectId || !configuredApiBase
          ? '/api/public/forms'
          : `${normalizeBaseUrl(configuredApiBase)}/api/projects/${encodeURIComponent(configuredProjectId)}/app/public/forms`;
      const response = await fetch(publicFormsUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            form_type: 'contact',
            name: typeof data.name === 'string' ? data.name : '',
            email: typeof data.email === 'string' ? data.email : '',
            phone: typeof data.phone === 'string' ? data.phone : '',
            message: typeof data.message === 'string' ? data.message : '',
            page_url: typeof window !== 'undefined' ? window.location.href : '',
          }),
        });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Form submission failed' }));
        throw new Error(error.message || 'Form submission failed');
      }

      return {
        success: true,
        message: 'Message sent successfully.',
      };
    }

    const workerUrl = `/api/forms/${formType}`;

    const response = await fetch(workerUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        type: formType,
        data: formData,
        timestamp: new Date().toISOString(),
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Form submission failed' }));
      throw new Error(error.message || 'Form submission failed');
    }

    const result = await response.json();
    return {
      success: true,
      message: result.message || 'Form submitted successfully',
    };
  } catch (error) {
    console.error('Form submission error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'An unknown error occurred',
    };
  }
}

